/**
 * All primitive colors for MealMap (light appearance).
 * Screens/components use `theme.colors.*` — palette is the single place for hex/rgba literals.
 * The native mobile shell uses Abbi Theme's token sheet (`abbiThemeLight` / `abbiThemeDark` below);
 * `palette` keeps the clinical web primitives plus legacy native-only colors not yet on the sheet.
 */
export const palette = {
    // Neutrals (trimmed: merged near-duplicates; unused steps removed)
    neutral0: '#FFFFFF',
    neutral35: '#fafafa',
    neutral45: '#f8f9fa',
    neutral48: '#f7f7f8',
    neutral49: '#f4f4f6', // web: keep in sync with index.html body background + index.web.js shell
    neutral50: '#f5f5f5',
    neutral55: '#f4f9ff',
    neutral60: '#f8fbff',
    neutral70: '#f1f2f6',
    neutral75: '#eef1f6',
    neutral80: '#eee',
    neutral85: '#e9ecef',
    neutral90: '#e6e6eb',
    neutral95: '#e0e0e0',
    neutral96: '#dadae0',
    neutral110: '#ddd',
    neutral120: '#ccc',
    neutral125: '#bbb',
    neutral130: '#b1b1b1',
    neutral140: '#aaa',
    neutral150: '#999999',
    neutral160: '#888888',
    neutral170: '#777777',
    neutral175: '#6E6E73',
    neutral176: '#8E8E93',
    neutral180: '#666666',
    neutral185: '#5c5c5c',
    neutral190: '#5A5A5F',
    neutral195: '#555555',
    neutral200: '#495057',
    neutral210: '#444444',
    neutral220: '#3A3A3F',
    neutral225: '#333333',
    neutral230: '#222222',
    neutral235: '#1c1c1e',
    neutral240: '#1a1a1a',
    neutral245: '#0f172a',
    neutral250: '#0b0b0f',
    neutral255: '#000000',
    neutral600BlueGrey: '#78909C',
    // Brand / links (align iOS-style blue with app brand where reasonable)
    brand500: '#0098EB', // web: keep in sync with index.html <meta name="theme-color">
    brand400: '#007bff',
    linkIos: '#007AFF',
    linkVariant: '#0078c8',
    linkStrong: '#0E7AFE',
    linkBright: '#0A60FF',
    linkSecondary: '#3B82F6',
    // Blue surfaces (info, portion gate, chips) — single light tint + borders
    blue50: '#E3F2FD',
    blue90: '#C7DBFF',
    blue100: '#BBDEFB',
    blue110: '#93c5fd',
    blue600: '#1976D2',
    blue700: '#1e88e5',
    blue750: '#90caf9',
    blue900: '#0D47A1',
    // Amber / orange (tips, validation gate, warm callouts)
    amber40: '#fff4e5',
    amber45: '#fff3e0',
    amber50: '#FFF9E6',
    amber55: '#fff5f5',
    amber200: '#FFE082',
    amber210: '#f6c98b',
    amber700: '#F57C00',
    amber750: '#ffb300',
    amber800: '#b45309',
    amber900: '#E65100',
    amber950: '#8a5200',
    // Green (success, weigh-in, positive deltas) — soft success = green55 only
    green55: '#e8f5e9',
    green100: '#C8E6C9',
    green400: '#4CAF50',
    green500: '#34C759',
    green600: '#388E3C',
    green650: '#2e7d32',
    green700: '#1B5E20',
    green750: '#1a5f1a',
    green800: '#1f7a48',
    green850: '#0a7a39',
    // Red / danger
    red50: '#ffebee',
    red100: '#fdecea',
    red400: '#FF5722',
    red500: '#FF3B30',
    red600: '#D32F2F',
    red650: '#c62828',
    red700: '#dc3545',
    red900: '#b00020',
    // Purple / cyan (charts)
    purple600: '#9C27B0',
    cyan500: '#00BCD4',
    // Yellow / amber chart
    yellow600: '#FFC107',
    chartOrange: '#FF9800',
    // Misc iOS-style
    iosIndigo: '#5856D6',
    // Side panel / CRM
    sidePanelBg: '#7780AF',
    // Sign-in eye on web
    eyeWeb: '#EFEFEF',
    // Bootstrap-style (UserInfo badges)
    bootstrapSuccess: '#28a745',
    // Shadows / overlays (rgba kept as primitives for dark mode tuning)
    shadowBlack: '#000000',
    /** CSS `rgba(r, g, b, a)` channels only (comma-separated) for box-shadow templates */
    blackRgbChannel: '0, 0, 0',
    transparent: 'transparent',
    overlayScrim30: 'rgba(0, 0, 0, 0.3)',
    overlayScrim50: 'rgba(0, 0, 0, 0.5)',
    overlayScrim70: 'rgba(0, 0, 0, 0.7)',
    overlayScrim10: 'rgba(0,0,0,0.1)',
    overlayScrim04: 'rgba(0,0,0,0.04)',
    overlayScrim06: 'rgba(0,0,0,0.06)',
    surfaceTranslucent90: 'rgba(255, 255, 255, 0.9)',
    scrimLight70: 'rgba(255, 255, 255, 0.7)',
    dangerTranslucent80: 'rgba(255, 0, 0, 0.8)',
    // Program phase fills (day-type pills). #384: Loss = green, Reward = purple.
    // Jay (Oct 2026): red stays for errors and warnings, so Reward is not red.
    // Text on fill: Loss #38761d on #d9ead3 4.4:1,
    // Reward #6A1B9A on #F3E5F5 7.8:1. Labels stay, so not color alone.
    phaseLossBg: '#d9ead3',
    phaseMaintBg: '#fff2cc',
    phaseRewardBg: '#f3e5f5',
    phaseLossFg: '#38761d',
    phaseMaintFg: '#b59b00',
    phaseRewardFg: '#6a1b9a',
    redGenerate: '#cc0000',
    // Avatar default (legacy)
    avatarBlue: '#3498db',
    // Web nav arrow (Meal Map pager)
    navArrowBgDefault: '#e8e8ed',
    navArrowBgHover: '#d1d1d6',
    // Disabled (#393 low-vision pass), iOS convention: same filled circle as
    // enabled, no outline, no fade; only the chevron goes lighter. #808085 is
    // ~3.2:1 on #e8e8ed (web) and ~3.1:1 on the native glass fill (#e4e3e0 over
    // #F9F7F2), vs ~13.8:1 / ~13.1:1 for the enabled #1d1d1f chevron.
    navArrowBgDisabled: '#e8e8ed',
    navArrowFgDefault: '#1d1d1f',
    navArrowFgDisabled: '#808085',
    // Web focus ring (react-select style)
    focusRingWeb: '#90ACF2',
    // Button disabled bg (fix legacy typo — was invalid hex)
    buttonDisabledBg: '#dfdfdf',
};
/**
 * Abbi Theme mobile token sheet (logo crimson + green, warm off-white), Oct 2026.
 * Light and dark are defined as pairs with identical keys. The app renders LIGHT only;
 * `abbiThemeDark` is not wired anywhere yet (no dark-mode switching).
 * Map these into `paperThemeColors.native.ts`; do not import them from screens.
 */
export const abbiThemeLight = {
    background: '#F9F7F2',
    card: '#FFFFFF',
    /** Banner, chip, pressed row. */
    tint: '#F3EEE6',
    /** Decorative only: 1pt card borders and row dividers. */
    hairline: '#E4DED3',
    textPrimary: '#172019',
    textSecondary: '#5C665C',
    /** Identity crimson (matches the wordmark artwork): active tab, day arrows, back. */
    accent: '#971E16',
    accentPressed: '#741711',
    /** Text on a solid accent fill (rare). */
    onAccent: '#FFFFFF',
    /** Primary buttons, + button fills. */
    action: '#247B3D',
    actionPressed: '#1B5F2F',
    onAction: '#FFFFFF',
    /** Inline text links, "Ready to log". */
    link: '#1F6F36',
    /** List-row disclosure chevrons. */
    rowChevron: '#7D867C',
    /** #384: Loss = green, Reward = purple (#6A1B9A on #F3E5F5). Red stays for errors. Labels always shown. */
    phaseLossFill: '#E6F0E5',
    phaseLossText: '#1B5F2F',
    phaseMaintFill: '#FCEFD9',
    phaseMaintText: '#7A4A00',
    phaseRewardFill: '#F3E5F5',
    phaseRewardText: '#6A1B9A',
    /** Home encouragement banner: sage fill, dark green text, action-green leaf. */
    encouragementFill: '#E6F0E5',
    encouragementText: '#1B5F2F',
    encouragementIcon: '#247B3D',
    /** Over target: icon + text, never color alone. */
    danger: '#B93A0C',
    dangerFill: '#FDECE4',
    dangerText: '#9A3009',
    /** Done marks, checks. */
    success: '#247B3D',
    /** 2pt focus ring. */
    focusRing: '#2B6CB0',
    tipFill: '#FDF1D8',
    tipText: '#172019',
    tipIcon: '#A65A00',
};
/** Dark pair for every light token. Defined for later; not rendered. */
export const abbiThemeDark = {
    background: '#1B1E19',
    card: '#242821',
    tint: '#2D322A',
    hairline: '#3A4036',
    textPrimary: '#F1F2EC',
    textSecondary: '#A9B1A6',
    accent: '#FF6770',
    accentPressed: '#FF8A91',
    onAccent: '#1B1E19',
    action: '#6DCE87',
    actionPressed: '#8ADBA0',
    onAction: '#10240F',
    link: '#6DCE87',
    rowChevron: '#879084',
    phaseLossFill: '#1F3324',
    phaseLossText: '#9FE0AF',
    phaseMaintFill: '#3A2E17',
    phaseMaintText: '#F5C978',
    // Reward purple (not rendered yet). Light text #E1BEE7 on fill #3A1F3A is 8.9:1.
    phaseRewardFill: '#3A1F3A',
    phaseRewardText: '#E1BEE7',
    encouragementFill: '#1F3324',
    encouragementText: '#9FE0AF',
    encouragementIcon: '#6DCE87',
    danger: '#FF8A5C',
    dangerFill: '#3A2318',
    dangerText: '#FFB08F',
    success: '#6DCE87',
    focusRing: '#7FB3FF',
    tipFill: '#352C17',
    tipText: '#F1F2EC',
    tipIcon: '#F5B54A',
};
